import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import { provideOAuthClient, OAuthService } from 'angular-oauth2-oidc';
import { authCodeFlowConfig } from '@v1/core/oauth/auth.config';
import { oauthResourceServerConfig } from '@v1/core/oauth/oauth-resource-server.config';
import { oauthTokenInterceptor } from '@v1/core/oauth/oauth-token.interceptor';
import { oauthErrorInterceptor } from '@v1/core/oauth/oauth-error.interceptor';
import { CoreInterceptor } from '@v1/features/enterprises/pages/layout/interceptors/core.interceptor';
import { AuthService } from '@v1/core/oauth/auth.service';
import { SessionMonitorService } from '@v1/core/oauth/session-monitor.service';
import { CentrifugoConnectionService } from '@v1/core/centrifugo/centrifugo-connection.service';

const WolopPreset = definePreset(Aura, {
  primitive: {
    action: palette('#00c666'),
  },
  semantic: {
    primary: palette('#2e5ba3'),
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{action.500}',
              hoverBackground: '{action.600}',
              activeBackground: '{action.700}',
              borderColor: '{action.500}',
              hoverBorderColor: '{action.600}',
              activeBorderColor: '{action.700}',
              focusRing: {
                color: '{action.500}',
              },
            },
          },
        },
      },
    },
    dialog: {
      root: {
        background: 'var(--wolop-surface)',
        borderColor: 'var(--wolop-border)',
        color: 'var(--wolop-text)',
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([oauthErrorInterceptor, oauthTokenInterceptor, CoreInterceptor]),
    ),
    MessageService,
    ConfirmationService,
    CentrifugoConnectionService,
    provideAppInitializer(() => {
      const oauthService = inject(OAuthService);
      const authService = inject(AuthService);
      oauthService.configure(authCodeFlowConfig);
      oauthService.setStorage(localStorage);
      authService.setupAutomaticSilentRefresh();
      inject(SessionMonitorService).start();
    }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    providePrimeNG({
      theme: {
        preset: WolopPreset,
        options: {
          darkModeSelector: '.app-dark',
        },
      },
    }),
    provideOAuthClient({
      resourceServer: oauthResourceServerConfig,
    }),
  ],
};
